import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/utils/logger";

interface SubscriptionContextType {
  subscribed: boolean;
  userType: 'buyer' | 'seller' | null;
  subscriptionStatus: string;
  productId: string | null;
  subscriptionEnd: string | null;
  isLoading: boolean;
  checkSubscription: () => Promise<void>;
  createCheckoutSession: (priceId: string, userType: 'buyer' | 'seller', promoCode?: string) => Promise<string>;
  openCustomerPortal: () => Promise<string>;
  cancelSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const [subscribed, setSubscribed] = useState(false);
  const [userType, setUserType] = useState<'buyer' | 'seller' | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const [productId, setProductId] = useState<string | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const checkSubscription = async (showLoading = true) => {
    try {
      if (showLoading) {
        setIsLoading(true);
      }
      logger.debug("Checking subscription status");
      
      const { data, error } = await supabase.functions.invoke('check-subscription');
      
      if (error) {
        logger.error("Subscription check error", { error });
        throw error;
      }

      if (data) {
        logger.debug("Subscription data received", data);
        setSubscribed(data.subscribed || false);
        setUserType(data.user_type || null);
        setSubscriptionStatus(data.subscription_status || '');
        setProductId(data.product_id || null);
        setSubscriptionEnd(data.subscription_end || null);
      }
    } catch (error: any) {
      logger.error("Failed to check subscription", { error: error.message });
      // Don't throw error to avoid breaking the app
      setSubscribed(false);
      setSubscriptionStatus('error');
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  };

  const createCheckoutSession = async (priceId: string, userType: 'buyer' | 'seller', promoCode?: string): Promise<string> => {
    try {
      logger.debug("Creating checkout session", { priceId, userType, promoCode });
      
      const body: any = { price_id: priceId, user_type: userType };
      if (promoCode) {
        body.promo_code = promoCode;
      }
      
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body
      });
      
      if (error) {
        logger.error("Checkout creation error", { error });
        throw error;
      }

      if (!data?.url) {
        throw new Error("No checkout URL received");
      }

      logger.debug("Checkout session created", { url: data.url });
      return data.url;
    } catch (error: any) {
      logger.error("Failed to create checkout session", { error: error.message });
      throw new Error(error.message || "Failed to create checkout session");
    }
  };

  const openCustomerPortal = async (): Promise<string> => {
    try {
      logger.debug("Opening customer portal");
      
      const { data, error } = await supabase.functions.invoke('customer-portal');
      
      if (error) {
        logger.error("Customer portal error", { error });
        let msg = error.message;
        try { msg = (await (error as any).context?.json())?.error || msg; } catch { /* ignore */ }
        throw new Error(msg);
      }

      if (!data?.url) {
        throw new Error("No portal URL received");
      }

      logger.debug("Customer portal session created", { url: data.url });
      return data.url;
    } catch (error: any) {
      logger.error("Failed to open customer portal", { error: error.message });
      throw new Error(error.message || "Failed to open customer portal");
    }
  };

  const cancelSubscription = async (): Promise<void> => {
    try {
      logger.debug("Cancelling subscription");
      
      const { data, error } = await supabase.functions.invoke('cancel-subscription');
      
      if (error || data?.success === false) {
        logger.error("Subscription cancellation error", { error, data });
        const err: any = new Error("cancel_failed");
        err.code = "cancel_failed";
        throw err;
      }

      logger.debug("Subscription cancelled successfully", data);
      
      // Refresh subscription status
      await checkSubscription();
    } catch (error: any) {
      logger.error("Failed to cancel subscription", { error: error.message });
      const err: any = new Error("cancel_failed");
      err.code = "cancel_failed";
      throw err;
    }
  };

  // Check subscription on auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session?.user) {
          // Small delay to ensure user profile is loaded (reduced for LCP optimization)
          setTimeout(() => {
            checkSubscription();
          }, 150);
        } else {
          // Clear subscription data when signed out
          setSubscribed(false);
          setUserType(null);
          setSubscriptionStatus('');
          setProductId(null);
          setSubscriptionEnd(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Check subscription every 60 seconds for active users
  useEffect(() => {
    const interval = setInterval(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        checkSubscription(false);
      }
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  return (
    <SubscriptionContext.Provider
      value={{
        subscribed,
        userType,
        subscriptionStatus,
        productId,
        subscriptionEnd,
        isLoading,
        checkSubscription,
        createCheckoutSession,
        openCustomerPortal,
        cancelSubscription
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
};